import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Pagination from 'components/Pagination';
import CreateRecipe from './modals/CreateRecipe';

import { GET, httpOk } from 'helpers/api';
import { formatDate, formatMoney } from 'helpers/helpers';
import { Link } from 'react-router-dom';

import { ReactComponent as BomIcon } from 'assets/icons/sidebar/bom.svg';
import { ReactComponent as ReportUpIcon } from 'assets/icons/report_up.svg';
import { ReactComponent as ProductionIcon } from 'assets/icons/production.svg';
import { ReactComponent as WarningCircleIcon } from 'assets/icons/warning_circle.svg';

function Dashboard() {
    const { t } = useTranslation()

    const initFilterData = {
        page: 1,
        page_size: 20,
    }

    const [createRecipeModal, setCreateRecipeModal] = useState(false)
    const [filterData, setFilterData] = useState(initFilterData)
    const [data, setData] = useState({
        in_progress_orders: [],
        planned_orders: [],
        counters: {},
    })

    const handlePaginate = (args) => {
        setFilterData(prev => ({
            ...prev,
            page: args.page + 1,
            page_size: args.page_size
        }));
    };

    const getData = useCallback(async () => {
        const [response, responseRecipes, responseOrders] = await Promise.all([
            GET('/recipes/dashboard/', filterData),
            GET('/recipes/', filterData),
            GET('/production/orders/?status=in_progress,planned', filterData),
        ]);

        if (httpOk(response)) {
            setData({ ...response.data, ...responseRecipes.data, orders: responseOrders.data });
        }
    }, [filterData]);

    useEffect(() => {
        getData();
    }, [getData]);

    return (
        <>
            <div>
                <div className="main-content-title">
                    {t('technologist_dashboard')}
                </div>

                <div className="main-content-subtitle">
                    {t('recipe_control_desc')}
                </div>
            </div>

            <div className="grid4 gap-16">
                <div className="card">
                    <div className="d-between">
                        <div>
                            <div className="title">
                                {t('active_recipes')}
                            </div>
                            <div className="value">
                                {formatMoney(data?.active_recipes)}
                            </div>
                        </div>

                        <div className="icon primary">
                            <BomIcon />
                        </div>
                    </div>
                </div>

                <div className="card">
                    <div className="d-between">
                        <div>
                            <div className="title">
                                {t('in_production')}
                            </div>
                            <div className="value text-purple">
                                {formatMoney(data?.in_production)}
                            </div>
                        </div>

                        <div className="icon purple">
                            <ProductionIcon />
                        </div>
                    </div>
                </div>

                <div className="card">
                    <div className="d-between">
                        <div>
                            <div className="title">
                                {t('require_attention')}
                            </div>
                            <div className="value text-warning">
                                {formatMoney(data?.attention_recipes)}
                            </div>
                        </div>

                        <div className="icon warning">
                            <WarningCircleIcon />
                        </div>
                    </div>
                </div>

                <div className="card">
                    <div className="d-between">
                        <div>
                            <div className="title">
                                {t('average_composition')}
                            </div>
                            <div className="value">
                                <div className="d-flex align-items-center gap-8">
                                    {data?.average_ingredients}
                                    <div className="fz12 text-second">
                                        {t('ingredients')}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="icon success">
                            <ReportUpIcon />
                        </div>
                    </div>
                </div>
            </div>

            {data?.deficit_recipes?.length > 0 &&
                <div className="alert second alert-warning">
                    <div className="d-flex align-items-center alert-title mb-32 gap-8">
                        <WarningCircleIcon />
                        {t('deficit_ingredients_alert')}
                    </div>

                    <div className="mb-16">
                        {t('low_raw_material_warning_desc')}
                    </div>

                    <div className="d-flex flex-column gap-8">
                        {data?.deficit_recipes?.map((item, index) => (
                            <div className="card second" key={index}>
                                <div className="d-between mb-10">
                                    <div>
                                        <div className="text-first">
                                            {item.product_name}
                                        </div>
                                        <div className="text-second">
                                            {item.sku}
                                        </div>
                                    </div>

                                    <div className="alert alert-warning">
                                        {item.deficit_count} {t('deficit_items')}
                                    </div>
                                </div>
                                <div className="d-flex gap-8 text-second">
                                    {t('problem_ingredients')}:

                                    <div className="text-fourth">
                                        {item?.deficit_ingredients?.map((item2, index2) => (
                                            <span key={index2}>
                                                {item2} {(index2 + 1) !== item?.deficit_ingredients.length && ', '}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ))
                        }
                    </div>

                    <Link to="/technologist/recipes">
                        <button className="dark mt-16">
                            {t('warehouse_management')}
                        </button>
                    </Link>
                </div>
            }

            <div className="card">
                <div className="d-between mb-30">
                    <div className="fz16 text-third">
                        {t('current_recipes')}
                    </div>

                    <Link to="/technologist/recipes">
                        <button className="dark">
                            {t('all_raw_materials')}
                        </button>
                    </Link>
                </div>

                <table className="table">
                    <thead>
                        <tr>
                            <th style={{ width: '10%' }}>{t('sku')}</th>
                            <th style={{ width: '30%' }}>{t('product')}</th>
                            <th style={{ width: '10%' }}>{t('version')}</th>
                            <th style={{ width: '10%' }}>{t('ingredients')}</th>
                            <th style={{ width: '20%' }}>{t('last_update')}</th>
                            <th style={{ width: '20%' }}>{t('technologist')}</th>
                        </tr>
                    </thead>

                    <tbody>
                        {data?.results?.map((item, index) => (
                            <tr key={index}>
                                <td className="text-second">{item.sku}</td>
                                <td>{item.product_name}</td>
                                <td>{item.current_version}</td>
                                <td>{formatMoney(item.ingredients_count)}</td>
                                <td>{formatDate(item.version_created_at)}</td>
                                <td>{item.technologist_name}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                <Pagination
                    paginate={handlePaginate}
                    pagination={{
                        total: data?.count || 0
                    }}
                />
            </div>


            <div className="card">
                <div className="d-between mb-30">
                    <div className="fz16 text-third">
                        {t('recipes_in_production')}
                    </div>

                    <Link to="/production/production">
                        <button className="dark">
                            {t('to_production')}
                        </button>
                    </Link>
                </div>

                <table className="table">
                    <thead>
                        <tr>
                            <th style={{ width: '10%' }}>{t('order_id')}</th>
                            <th style={{ width: '50%' }}>{t('product')}</th>
                            <th style={{ width: '10%' }}>{t('volume')}</th>
                            <th style={{ width: '30%' }}>{t('status')}</th>
                        </tr>
                    </thead>

                    <tbody>
                        {data?.orders?.map((item, index) => (
                            <tr key={index}>
                                <td className="text-second">{item.order_number}</td>
                                <td>
                                    <div>
                                        {item.product_name}
                                    </div>
                                </td>
                                <td>{formatMoney(item.planned_quantity)} {item.unit_short_name}</td>
                                <td>
                                    <div className={`alert alert-${item.status} w-fit-content`}>
                                        {t(item.status)}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                <Pagination
                    paginate={handlePaginate}
                    pagination={{
                        total: data?.count || 0
                    }}
                />
            </div>

            <div className="card">
                <div className="mb-30 fz16 text-third">
                    {t('quick_actions')}
                </div>

                <div className="d-flex gap-16 w-100">
                    <button className="dark second" onClick={() => setCreateRecipeModal(true)}>
                        <BomIcon />
                        {t('create_recipe')}
                    </button>

                    <Link to="/technologist/recipes" className="w-100">
                        <button className="white second">
                            <ProductionIcon />
                            {t('edit_recipe')}
                        </button>
                    </Link>

                    <Link to="/production/production" className="w-100">
                        <button className="white second">
                            <WarningCircleIcon />
                            {t('production_analysis')}
                        </button>
                    </Link>
                </div>
            </div>

            <CreateRecipe modal={createRecipeModal} setModal={setCreateRecipeModal} />
        </>
    )
}

export default Dashboard